import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { useToast } from '@/shared/hooks';
import { saveAsTemplateSchema } from '../schemas';
import type { SaveAsTemplateFormValues } from '../schemas';
import { useSaveProjectAsTemplate } from '../hooks/useSaveProjectAsTemplate';
import { TEMPLATE_SECTOR } from '../types';

interface SaveAsTemplateDialogProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SaveAsTemplateDialog({
  projectId,
  projectName,
  open,
  onOpenChange,
}: SaveAsTemplateDialogProps) {
  const { t } = useTranslation('projectTemplates');
  const { toast } = useToast();
  const saveAsTemplate = useSaveProjectAsTemplate(projectId);

  const { control, handleSubmit, reset } = useForm<SaveAsTemplateFormValues>({
    resolver: zodResolver(saveAsTemplateSchema),
    defaultValues: { title: projectName, sector: TEMPLATE_SECTOR.Technology, description: '' },
  });

  useEffect(() => {
    if (open) reset({ title: projectName, sector: TEMPLATE_SECTOR.Technology, description: '' });
  }, [open, projectName, reset]);

  const sectorOptions = Object.values(TEMPLATE_SECTOR).map((sector) => ({
    value: sector,
    label: t(`gallery.sectorFilter.${sector}`),
  }));

  function onSubmit(values: SaveAsTemplateFormValues) {
    saveAsTemplate.mutate(values, {
      onSuccess: () => {
        toast.success({ title: t('saveAsTemplate.toast.success') });
        onOpenChange(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full rounded-none md:max-w-[480px] md:rounded-lg"
        data-testid="project-templates-save-as-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('saveAsTemplate.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('saveAsTemplate.title')}</DialogDescription>
        </DialogHeader>

        <form
          id="save-as-template-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <FormFieldInput
            control={control}
            name="title"
            label={t('editor.fields.name')}
            floatingLabel
            disabled={saveAsTemplate.isPending}
            data-testid="project-templates-save-as-name-input"
          />
          <FormFieldSelect
            control={control}
            name="sector"
            label={t('editor.fields.sector')}
            floatingLabel
            options={sectorOptions}
            disabled={saveAsTemplate.isPending}
            data-testid="project-templates-save-as-sector-select"
          />
          <FormFieldTextarea
            control={control}
            name="description"
            label={t('editor.fields.description')}
            floatingLabel
            disabled={saveAsTemplate.isPending}
            data-testid="project-templates-save-as-description-input"
          />
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saveAsTemplate.isPending}
          >
            {t('saveAsTemplate.cancel')}
          </Button>
          <Button
            type="submit"
            form="save-as-template-form"
            disabled={saveAsTemplate.isPending}
            data-testid="project-templates-save-as-submit-button"
          >
            {saveAsTemplate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('saveAsTemplate.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
